import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Toast from './Toast'
import Modal from './Modal'

interface AffiliateProviderChoiceProps {
  onClose: (provider: string) => void
}

export default function AffiliateProviderChoice({ onClose }: AffiliateProviderChoiceProps) {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showSuccessToast, setShowSuccessToast] = useState(false)

  const handleSave = async () => {
    if (!selectedProvider) {
      setError('Please select a provider')
      return
    }

    setSaving(true)
    setError('')

    try {
      const response = await fetchWithCsrf('/api/v1/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            preferred_affiliate_provider: selectedProvider
          }
        })
      })

      if (response.ok) {
        // Close immediately and pass the provider to parent
        onClose(selectedProvider)
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || data.error || 'Failed to save preference')
        setSaving(false)
      }
    } catch (err) {
      setError('Failed to save preference. Please try again.')
      setSaving(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={() => {}} size="md" showCloseButton={false}>
        <div className="p-8 relative">
          <div className="mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Book Provider</h2>
            <p className="text-gray-600">Where should "Buy Book" buttons link to?</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            {/* Bookshop.org Option */}
            <label
              className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                selectedProvider === 'bookshop'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value="bookshop"
                checked={selectedProvider === 'bookshop'}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="mt-1 mr-3 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900 mb-1">Bookshop.org</div>
                <div className="text-sm text-gray-600">Support your local bookstores</div>
              </div>
            </label>

            {/* Amazon Option */}
            <label
              className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                selectedProvider === 'amazon'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value="amazon"
                checked={selectedProvider === 'amazon'}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="mt-1 mr-3 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900 mb-1">Amazon</div>
                <div className="text-sm text-gray-600">Not local, but often lower prices</div>
              </div>
            </label>
          </div>

          <p className="text-xs text-gray-500 mb-6 text-center">
            You can change this anytime in your profile settings.
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={!selectedProvider || saving}
              className={`flex-1 py-3 px-6 font-semibold rounded-lg transition-all shadow-lg ${
                saving
                  ? 'bg-green-500 text-white shadow-green-500/30'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved!
                </span>
              ) : 'Continue'}
            </button>
          </div>
        </div>
      </Modal>

      {showSuccessToast && (
        <Toast
          message="Preference saved!"
          type="success"
          onClose={() => setShowSuccessToast(false)}
        />
      )}
    </>
  )
}
